import type { ConnectError } from '@connectrpc/connect'
import {
  DemandProcurementMasterErrorReason,
  DemandProcurementMasterErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  type AppErrorInfoResolution,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'

/**
 * `DemandProcurementMasterErrorReason`
 * (occto-plan-master-reason-registry 設計書, backend `occtomaster` domain)
 * → i18n key + field routing registry for the DemandProcurementMaster
 * Create/Update modals (mirrors BgMaster/models/serverError.ts, decision
 * E). See that file's `BG_MASTER_REASON_KEYS` godoc for the `Record<Enum,
 * AppErrorInfoReasonEntry | undefined>` compile-time-coverage contract and
 * the `field` routing rationale (docs/adr/
 * structured-app-error-standard-errorinfo-envelope.md decision 2).
 *
 * D3/D4: `REP_BG_NOT_FOUND` (代表BG select) is the only field-scoped entry
 * (field=`bgMasterId`); license mismatch / duplicate are operation-scoped.
 * D6: keys live under this tab's own `serverError.reason.*` sub-namespace
 * to disambiguate reason names that also exist on sibling plan-master
 * tabs (e.g. GSP/DSP both have `submitterNotFound`).
 */
const DEMAND_PROCUREMENT_MASTER_REASON_KEYS: Record<
  DemandProcurementMasterErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [DemandProcurementMasterErrorReason.UNSPECIFIED]: undefined,
  [DemandProcurementMasterErrorReason.REP_BG_NOT_FOUND]: {
    key: 'page:adminOcctoMaster.demandProcurementPlanMaster.serverError.reason.repBgNotFound',
    field: 'bgMasterId',
  },
  [DemandProcurementMasterErrorReason.REP_BG_LICENSE_MISMATCH]: {
    key: 'page:adminOcctoMaster.demandProcurementPlanMaster.serverError.reason.repBgLicenseMismatch',
  },
  // AC-OMC-004b: reuses the pre-existing spec-worded duplicate key (the
  // dynamic `{{bgCode}}` interpolation comes from the resolver's `values`
  // argument, D1) rather than minting a second copy of the same text
  // under `serverError.reason.*`.
  [DemandProcurementMasterErrorReason.BG_ALREADY_HAS_PROCUREMENT_MASTER]: {
    key: 'page:adminOcctoMaster.demandProcurementPlanMaster.serverError.duplicateBg',
  },
}

/**
 * `resolveDetailedError` for DemandProcurementMaster's `useCrudModalForm`
 * (Create / Update). Resolves the `google.rpc.ErrorInfo` Connect error
 * detail, if any, against `DEMAND_PROCUREMENT_MASTER_REASON_KEYS` via the
 * `DemandProcurementMasterErrorReasonSchema` reflection lookup. `values`
 * is passed through to `t(key, values)` for the duplicate reason's
 * `{{bgCode}}` interpolation (D1); callers must source it from the fresh
 * submitted form values (see useCrudModalForm's resolveDetailedError
 * contract, N3).
 */
export function resolveDemandProcurementMasterDetailedError(
  err: ConnectError,
  t: TFunction,
  values: Record<string, unknown>,
): AppErrorInfoResolution | undefined {
  return resolveAppErrorInfoMessage(
    err,
    DemandProcurementMasterErrorReasonSchema,
    DEMAND_PROCUREMENT_MASTER_REASON_KEYS,
    t,
    values,
  )
}
